import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  Globe, Smartphone, Copy, MapPin, LocateFixed, Building2,
} from 'lucide-react'
import { Label } from '@meno/ui/components/ui/label'
import { Switch } from '@meno/ui/components/ui/switch'
import { updateScreen } from '@meno/ui/lib/api'
import { ScreenStatusDot, screenDisplayStatus, type ScreenStatusLike } from '@meno/ui/components/shared/ScreenStatusBadge'
import { SectionSurface, SECTION_STACK } from './settings-primitives'
import LocationMapPicker from './LocationMapPicker'

type ScreenLike = ScreenStatusLike & {
  id: number
  public_id: string
  status: string
  effective_status?: string
  disconnect_at?: string | null
  location?: string
  resolution?: string
  device_kind?: 'android' | 'web'
  device_model?: string
  os_version?: string
  latitude?: number
  longitude?: number
}

// Location source for the read-only map: the branch the screen belongs to.
// Branches carry the deliberate, operator-set address/coords; a screen only
// gets device-reported GPS, which it may never have. Resolving from the
// branch means the map is stable and not editable from the screen page.
type BranchLike = {
  name?: string
  address?: string
  city?: string
  latitude?: number
  longitude?: number
}

export interface PropertiesColumnProps {
  screen:      ScreenLike
  branch?:     BranchLike
  isActive:    boolean
  setIsActive: (v: boolean) => void
  /** Fires after a map click successfully saves the screen's new
   *  latitude/longitude, so the caller can refresh its own `screen` state
   *  (the saved coords otherwise only live in this component's local view). */
  onLocationSaved?: () => void
  locationOnly?: boolean
}

export default function PropertiesColumn({
  screen, branch, isActive, setIsActive, onLocationSaved, locationOnly = false,
}: PropertiesColumnProps) {
  const { t } = useTranslation()
  const isWebPlayer = (screen.device_kind ?? 'android') === 'web'
  const playerLabel = isWebPlayer ? t('screens.webPlayer', 'Web Player') : t('screens.androidPlayer', 'Android Player')
  const playerSubtitle = isWebPlayer ? 'Browser' : (screen.device_model || 'Android')

  // Resolve the location shown on the map. Prefer the screen's OWN pinned
  // coordinates (device-reported, or now operator-set by clicking the map)
  // when present; otherwise fall back to the branch's coordinates, then its
  // address text (map has nothing to show a pin for in that case — it just
  // centers on the default view until the operator clicks to set one).
  const coords =
    screen.latitude != null && screen.longitude != null
      ? { lat: screen.latitude, lng: screen.longitude }
      : branch?.latitude != null && branch?.longitude != null
        ? { lat: branch.latitude, lng: branch.longitude }
        : null
  const addressLabel =
    screen.location
    || [branch?.address, branch?.city].filter(Boolean).join(', ')
    || branch?.name
    || ''

  const [savingLocation, setSavingLocation] = useState(false)
  const handlePickLocation = async (lat: number, lng: number) => {
    setSavingLocation(true)
    try {
      await updateScreen(screen.public_id, { latitude: lat, longitude: lng })
      toast.success(t('screens.locationSaved', 'Location saved'))
      onLocationSaved?.()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('screens.locationSaveFailed', 'Could not save location.'))
    } finally {
      setSavingLocation(false)
    }
  }

  // "Use my location" — a one-click alternative to hunting the pin down by
  // hand. Reads the BROWSER's current position (i.e. wherever the operator
  // is standing, which for an on-site setup is the screen itself or close
  // to it) via the standard Geolocation API, then saves it exactly like a
  // map click. On denial/failure/unsupported browsers this is a no-op — it
  // never clears the map, so whatever location was saved last stays put.
  const [locating, setLocating] = useState(false)
  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t('screens.geolocationUnsupported', 'Location is not supported in this browser.'))
      return
    }
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false)
        void handlePickLocation(pos.coords.latitude, pos.coords.longitude)
      },
      () => {
        setLocating(false)
        toast.error(t('screens.geolocationFailed', 'Could not get your location.'))
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    )
  }

  // "Use screen location" — commit the location this screen already sits at
  // by inheritance. Until an operator sets a pin, the panel DISPLAYS the
  // branch's address/coords as a fallback but the screen row itself stores
  // nothing, so anything reading screen.latitude directly (exports, the
  // telemetry map pin, the world-map placement) sees no location at all.
  // This makes the inherited value explicit with one click.
  //
  // The device cannot supply this: its heartbeat carries timezone, uptime,
  // latency and battery, but no GPS. The branch is the only location the
  // system actually knows for a screen the operator has not pinned by hand.
  const branchCoords =
    branch?.latitude != null && branch?.longitude != null
      ? { lat: branch.latitude, lng: branch.longitude }
      : null
  // Hidden once the screen carries its own pin — at that point it would either
  // be a no-op or would silently throw away the operator's deliberate choice.
  const canUseBranchLocation =
    branchCoords != null && (screen.latitude == null || screen.longitude == null)
  const useScreenLocation = () => {
    if (!branchCoords) return
    void handlePickLocation(branchCoords.lat, branchCoords.lng)
  }

  const copyLocation = () => {
    if (!coords) return
    void navigator.clipboard?.writeText(`${coords.lat}, ${coords.lng}`)
    toast.success(t('common.copied', 'Copied'))
  }

  return (
    <div className={SECTION_STACK}>
      {/* ── DEVICE — one section grouping the screen's identity + hardware:
          the Screen ID, online status, player type, and inherited location.
          Each row is divided so the block reads as a single "device" card. */}
      <SectionSurface className="overflow-hidden">
        {!locationOnly && (<>
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground px-4 pt-4 pb-2 font-semibold">
          {t('screens.device', 'Device')}
        </p>

        {/* Screen ID — the opaque public_id (UUID) is what every API path uses
            (/screens/{public_id}/...). Copies the UUID; the row truncates. */}
        <div className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{t('screens.screenId', 'Screen ID')}</p>
            <p className="text-sm font-mono truncate" title={screen.public_id}>{screen.public_id}</p>
          </div>
          <button
            type="button"
            onClick={() => { void navigator.clipboard?.writeText(screen.public_id); toast.success(t('common.copied', 'Copied')) }}
            className="w-8 h-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
            aria-label={t('common.copy', 'Copy')}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="border-t border-[hsl(var(--border-control))]" />

        {/* Online status + Active toggle. Effective status folds in license
            expiry so a license-ended screen reads "Expired" (red), not "online".
            "license_fallback" (Enterprise-only) is the gracefully-degraded
            counterpart — same disconnect_at-in-the-past fact, but the screen
            stayed paired and is showing the fallback image, so it gets the
            calm blue tone instead of the destructive red. */}
        {(() => {
          // Derive from the EDIT BUFFER's isActive, not the persisted row.
          // The toggle beside this label writes to the buffer (it commits on
          // Publish), while the status read `screen` — so flipping Active moved
          // the switch and left the word next to it saying "online" until a
          // save and a refetch landed. The operator flips it, sees nothing
          // change, and flips it again. The screen object still supplies
          // everything else (licence expiry, pairing), so the licence states
          // keep outranking this exactly as before.
          const live = { ...screen, is_active: isActive }
          const ds = screenDisplayStatus(live)
          return (
        <div className="px-4 py-3 flex items-center gap-2">
          <ScreenStatusDot screen={live} />
          <span className={`text-sm font-semibold capitalize ${ds === 'expired' ? 'text-destructive' : ds === 'license_fallback' ? 'text-blue-500' : ''}`}>
            {ds === 'expired' ? t('common.expired', 'Expired') : t(`common.${ds}`, ds)}
          </span>
          <div className="ms-auto flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">{t('common.active', 'Active')}</Label>
            <Switch checked={isActive} onCheckedChange={setIsActive} className="data-[state=checked]:bg-primary" />
          </div>
        </div>
          ) })()}

        <div className="border-t border-[hsl(var(--border-control))]" />

        {/* Player type */}
        <div className="px-4 py-3 flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-muted/40 border border-border flex items-center justify-center shrink-0">
            {isWebPlayer ? <Globe className="w-5 h-5 text-muted-foreground" /> : <Smartphone className="w-5 h-5 text-muted-foreground" />}
          </div>
          <div className="min-w-0 flex flex-col">
            <p className="text-sm font-semibold">{playerLabel}</p>
            <p className="text-xs text-muted-foreground truncate">{playerSubtitle}</p>
            <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground">
              {screen.resolution && <span>{screen.resolution}</span>}
              {screen.os_version && <span className="truncate">{screen.os_version}</span>}
            </div>
          </div>
        </div>

        <div className="border-t border-[hsl(var(--border-control))]" />
        </>)}

        {/* Location — click the map to drop/move a pin; saves immediately
            (no separate confirm step) via updateScreen. Falls back to
            showing the branch's address/coordinates until the operator sets
            one of their own. */}
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('screens.location', 'Location')}</p>
            <div className="flex items-center gap-3 shrink-0">
              {canUseBranchLocation && (
                <button
                  type="button"
                  onClick={useScreenLocation}
                  disabled={savingLocation}
                  title={t('screens.useScreenLocationHint', "Use the branch's saved location")}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none"
                >
                  <Building2 className="w-3 h-3" />
                  {t('screens.useScreenLocation', 'Use screen location')}
                </button>
              )}
              <button
                type="button"
                onClick={useMyLocation}
                disabled={locating || savingLocation}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                <LocateFixed className={`w-3 h-3 ${locating ? 'animate-pulse' : ''}`} />
                {locating ? t('screens.locating', 'Locating…') : t('screens.useMyLocation', 'Use my location')}
              </button>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">{t('screens.clickMapToSet', 'Click the map to set')}</p>
          {addressLabel && (
            <p className="text-sm flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <span>{addressLabel}</span>
            </p>
          )}
          <LocationMapPicker
            lat={coords?.lat}
            lng={coords?.lng}
            onPick={handlePickLocation}
            saving={savingLocation}
          />
          {coords && (
            <div className="flex items-center justify-between gap-2">
              <p className="text-[10px] font-mono text-muted-foreground">
                {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
              </p>
              <button
                type="button"
                onClick={copyLocation}
                className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                aria-label={t('screens.copyLocation', 'Copy coordinates')}
                title={t('screens.copyLocation', 'Copy coordinates')}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </SectionSurface>
      {/* License / subscription + the Danger Zone (Transfer / Delete) moved to
          the Management tab — see ManagementColumn. */}
    </div>
  )
}
